// ========================================
// CONFIGURAÇÃO DE AUTENTICAÇÃO OAUTH2
// ========================================

import { getHostRealm } from "@/embed/host";

export const KEYCLOAK_LOGIN_URL =
  typeof import.meta !== "undefined" &&
  (import.meta as any).env?.VITE_KEYCLOAK_LOGIN_URL;

const env =
  (typeof import.meta !== "undefined" && (import.meta as any).env) || {};

// Realm Generator (geradores e internos limitados)
const KEYCLOAK_AUTH_URL = env.VITE_KEYCLOAK_AUTH_URL;

/**
 * Client OIDC do Portal no realm `generator`. Exportado porque também nomeia a
 * chave das permissões dentro do token: `resource_access[CLIENT_ID].roles`.
 */
export const CLIENT_ID = env.VITE_KEYCLOAK_CLIENT_ID || "portal-gerador";

// Realm Musa (usuários internos) — mesmo client, realm diferente
const KEYCLOAK_MUSA_AUTH_URL = env.VITE_KEYCLOAK_MUSA_AUTH_URL;

/**
 * Retorna dinamicamente o redirect_uri baseado no origin atual.
 * Isso permite funcionar em qualquer ambiente sem configuração adicional.
 */
function getRedirectUri(path: string = "/login-redirect"): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}${path}`;
  }
  // Fallback para SSR ou ambientes onde window não está disponível
  return env.VITE_REDIRECT_URI || `http://localhost:8080${path}`;
}

export type KeycloakRealm = "generator" | "musa";

/**
 * Realm do último login bem-sucedido. Vive em localStorage (e não em
 * sessionStorage, onde ficam os tokens) porque precisa ser visível para abas
 * novas, que nascem sem token.
 *
 * Não decide qual formulário de login mostrar — só em qual realm **tentar** a
 * reautenticação silenciosa (`loginSilently`). Quando não há sessão SSO viva
 * ali, o fluxo cai no formulário do `generator`, nunca no do `musa`. Não é
 * credencial, é só roteamento.
 */
const LAST_REALM_KEY = "keycloak_last_realm";

export function setLastRealm(realm: KeycloakRealm): void {
  try {
    localStorage.setItem(LAST_REALM_KEY, realm);
  } catch {
    // Storage indisponível (modo privado, cota): seguimos com o padrão.
  }
}

/**
 * Esquece o realm. Chamado no logout e quando a tentativa silenciosa falha —
 * nos dois casos não há sessão viva no realm antigo, então tentar de novo só
 * gastaria um round-trip.
 */
export function clearLastRealm(): void {
  try {
    localStorage.removeItem(LAST_REALM_KEY);
  } catch {
    // Storage indisponível: nada a limpar.
  }
}

export function getLastRealm(): KeycloakRealm {
  // Embutido, quem manda é o host: ele sabe em qual realm o usuário ENTROU
  // agora. O localStorage daqui está vazio dentro do iframe — nunca houve
  // login por este caminho —, então cairia no padrão `generator` e recusaria
  // o interno que entrou pelo `musa`.
  const doHost = getHostRealm();
  if (doHost) return doHost;

  try {
    return localStorage.getItem(LAST_REALM_KEY) === "musa"
      ? "musa"
      : "generator";
  } catch {
    return "generator";
  }
}

/**
 * Marca, por aba, que a reautenticação silenciosa já foi tentada.
 *
 * Trava anti-loop: `prompt=none` pode voltar com `error=login_required`, e sem
 * essa marca o `AuthProvider` mandaria pro Keycloak de novo, indefinidamente.
 * Fica em sessionStorage porque a tentativa é por aba, igual aos tokens. É
 * limpa só quando um login dá certo — falha deixa a marca de pé de propósito,
 * pra qualquer caminho que volte aqui já ir pro formulário interativo.
 */
const SILENT_AUTH_KEY = "keycloak_silent_auth_attempted";

export function markSilentAuthAttempted(): void {
  try {
    sessionStorage.setItem(SILENT_AUTH_KEY, "1");
  } catch {
    // Sem storage não há como travar o loop: o chamador trata como "já
    // tentou" e vai direto pro login interativo.
  }
}

export function wasSilentAuthAttempted(): boolean {
  try {
    return sessionStorage.getItem(SILENT_AUTH_KEY) === "1";
  } catch {
    return true;
  }
}

export function clearSilentAuthAttempt(): void {
  try {
    sessionStorage.removeItem(SILENT_AUTH_KEY);
  } catch {
    // Nada a limpar.
  }
}

/** Erros que o Keycloak devolve para `prompt=none` quando precisa de interação. */
const SILENT_AUTH_ERRORS = [
  "login_required",
  "interaction_required",
  "consent_required",
  "account_selection_required",
];

export function isSilentAuthError(error: string | null | undefined): boolean {
  return !!error && SILENT_AUTH_ERRORS.includes(error);
}

/**
 * Inicia o fluxo OAuth2 no Realm Generator, com formulário de login.
 *
 * É o destino de todo usuário não autenticado que não tem sessão SSO viva,
 * inclusive quando o login anterior era do realm `musa`: o portal nunca prende
 * ninguém no formulário do `musa`. Usuário interno entra pela rota pública
 * `/login/musa`.
 */
export function login(): void {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: getRedirectUri("/login-redirect"),
    scope: "openid profile email",
  });
  window.location.href = `${KEYCLOAK_AUTH_URL}?${params.toString()}`;
}

/**
 * Inicia o fluxo OAuth2 no Realm Musa (usuários internos).
 * Rota: /login/musa → redireciona para Keycloak Realm Musa.
 */
export function loginMusa(): void {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: getRedirectUri("/login-redirect/musa"),
    scope: "openid profile email",
  });
  window.location.href = `${KEYCLOAK_MUSA_AUTH_URL}?${params.toString()}`;
}

/**
 * Reautenticação silenciosa no realm do último login, via `prompt=none`.
 *
 * É o que mantém logada a aba nova aberta a partir de um link do menu: ela
 * nasce sem token (sessionStorage é por aba), mas o cookie de SSO do Keycloak
 * é do navegador. Com a sessão viva, o Keycloak devolve o código na hora, sem
 * formulário; sem ela, devolve `error=login_required` no redirect_uri — e o
 * callback trata isso caindo no `login()` do generator.
 *
 * É esse round-trip que distingue "aba nova de quem está logado" de "sessão
 * morta", que são indistinguíveis olhando só o storage local: nos dois casos a
 * aba não tem token.
 */
export function loginSilently(): void {
  const realm = getLastRealm();
  const isMusa = realm === "musa";
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: getRedirectUri(
      isMusa ? "/login-redirect/musa" : "/login-redirect",
    ),
    scope: "openid profile email",
    prompt: "none",
  });
  const authUrl = isMusa ? KEYCLOAK_MUSA_AUTH_URL : KEYCLOAK_AUTH_URL;
  window.location.href = `${authUrl}?${params.toString()}`;
}

/**
 * Faz logout no Keycloak no realm que emitiu o token atual.
 * Revoga o refresh_token através de POST no endpoint de revogação.
 * Deve ser chamado após limpar tokens; recebe o realm do token para usar a URL correta
 * (não há refresh cruzado: logout no realm de origem).
 */
export async function logoutFromKeycloak(
  realm: KeycloakRealm = "generator",
  refreshToken: string | null = null,
): Promise<void> {
  // Endpoint de revogação de tokens (RFC 7009) - diferente do endpoint /logout
  // O /logout é para GET com redirect; o /revoke é para POST com token.
  const logoutUrl =
    realm === "musa"
      ? env.VITE_KEYCLOAK_MUSA_LOGOUT_URL
      : env.VITE_KEYCLOAK_LOGOUT_URL;

  const KEYCLOAK_REVOCATION_URL = logoutUrl.replace(/\/logout$/, "/revoke");

  // 1. Revogar refresh_token no Keycloak (invalida os tokens emitidos)
  if (refreshToken) {
    try {
      const res = await fetch(KEYCLOAK_REVOCATION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: CLIENT_ID,
          token: refreshToken,
          token_type_hint: "refresh_token",
        }).toString(),
      });
      if (!res.ok) {
        console.warn(
          "Revogação de token retornou",
          res.status,
          await res.text(),
        );
      }
    } catch (error) {
      console.error("Erro ao revogar token no Keycloak:", error);
    }
  }

  // 2. Redirecionar para logout do Keycloak (encerra sessão no servidor)
  // Isso evita que, ao clicar em "Entrar" de novo, o usuário seja logado sem senha
  // if (typeof window !== "undefined") {
  //   const postLogoutRedirect =
  //     typeof window.location.origin === "string"
  //       ? `${window.location.origin}/`
  //       : "/";
  //   const logoutParams = new URLSearchParams({
  //     client_id: CLIENT_ID,
  //     post_logout_redirect_uri: postLogoutRedirect,
  //   });
  //   window.location.href = `${logoutUrl}?${logoutParams.toString()}`;
  // }
}
